import React, { useState } from 'react';
import { Box, Typography, Tooltip, IconButton, Link } from '@mui/material';
import { ContentCopy as CopyIcon } from '@mui/icons-material';

interface CopyableTextProps {
    text: string;
    onTextClick?: (e: React.MouseEvent) => void;
    tooltipTitle?: string;
    sx?: any;
}

/**
 * CopyableText Component
 * Displays text with a copy icon and optional click handler
 */
export const CopyableText: React.FC<CopyableTextProps> = ({
    text,
    onTextClick,
    tooltipTitle = 'Copy to clipboard',
    sx,
}) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = (e: React.MouseEvent) => {
        e.stopPropagation();

        if (navigator.clipboard) {
            // Use the modern Clipboard API in secure contexts (HTTPS or localhost)
            navigator.clipboard.writeText(text)
                .then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                })
                .catch(err => console.error('Failed to copy text: ', err));
        } else {
            // Fallback path for non-secure contexts (HTTP)
            const textArea = document.createElement('textarea');
            textArea.value = text;
            
            // Prevent scrolling
            textArea.style.top = '0';
            textArea.style.left = '0';
            textArea.style.position = 'fixed';
            
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            
            try {
                document.execCommand('copy');
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } catch (err) {
                console.error('Fallback: Oops, unable to copy', err);
            }
            
            document.body.removeChild(textArea);
        }
    };

    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, ...sx }}>
            {onTextClick ? (
                <Link
                    component="button"
                    variant="body2"
                    onClick={onTextClick}
                    sx={{
                        cursor: 'pointer',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' },
                        textAlign: 'left',
                        color: 'inherit',
                        fontWeight: 'inherit',
                        fontSize: 'inherit',
                    }}
                >
                    {text}
                </Link>
            ) : (
                <Typography
                    variant="body2"
                    sx={{
                        color: 'inherit',
                        fontWeight: 'inherit',
                        fontSize: 'inherit',
                    }}
                >
                    {text}
                </Typography>
            )}
            <Tooltip title={copied ? 'Copied!' : tooltipTitle} arrow>
                <IconButton
                    size="small"
                    onClick={handleCopy}
                    sx={{
                        padding: 0.25,
                        color: 'text.secondary',
                        '&:hover': {
                            color: 'primary.main',
                        }
                    }}
                >
                    <CopyIcon sx={{ fontSize: '0.9rem' }} />
                </IconButton>
            </Tooltip>
        </Box>
    );
};
